import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers, faIdCard, faClock, faCoins, faCircleCheck, faTriangleExclamation,
    faSearch, faFilter, faSyncAlt, faPlus,
    faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import { Card, Typography, Button, Input } from "@material-tailwind/react";
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../common/ConfirmModal';
import Alerty from '../common/Alerty';
import { fixResizeObserverError } from '../../utils/resizeObserverFix';
import DriversTable from './drivers/DriversTable';
import DriverLicenseVerification from './drivers/DriverLicenseVerification';

const { rootUrl } = require('../../config/config');

// Status configuration - extracted for reusability
const STATUS_OPTIONS = [
    { value: 'AVAILABLE', label: 'Available' },
    { value: 'ASSIGNED', label: 'Assigned' },
    { value: 'OFF DUTY', label: 'Off Duty' },
    { value: 'ON BREAK', label: 'On Break' },
    { value: 'ON LEAVE', label: 'On Leave' },
    { value: 'UNDER TRAINING', label: 'Under Training' },
    { value: 'UNAVAILABLE', label: 'Unavailable' },
    { value: 'SUSPENDED', label: 'Suspended' },
    { value: 'RETIRED', label: 'Retired' },
    { value: 'AWAITING ASSIGNMENT', label: 'Awaiting Assignment' }
];

// Table columns configuration
const TABLE_COLUMNS = [
    { value: 'name', label: 'Driver Name' },
    { value: 'phone', label: 'Phone' },
    { value: 'id_no', label: 'ID No' },
    { value: 'license_no', label: 'License No' },
    { value: 'license_expiry', label: 'License Expiry' },
    { value: 'status', label: 'Status', useChip: true },
    { value: 'bal', label: 'Balance', class: 'text-right', money: true },
    { value: 'action', label: 'Actions' }
];

// Helper functions
// eslint-disable-next-line no-unused-vars
const getLicenseStatus = (expiryDate) => {
    if (!expiryDate || expiryDate === 'no licence exp') {
        return { status: 'no-data', color: 'text-gray-500' };
    }

    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
        return { status: 'expired', color: 'text-red-600 font-bold' };
    } else if (daysUntilExpiry <= 30) {
        return { status: 'expiring-soon', color: 'text-orange-600 font-semibold' };
    }
    return { status: 'valid', color: 'text-brand-success' };
};

// eslint-disable-next-line no-unused-vars
const getBalanceStatus = (balance) => {
    const bal = parseFloat(balance) || 0;

    if (bal < 0) return { status: 'overpaid', color: 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-md' };
    if (bal <= 1000) return { status: 'excellent', color: 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-md' };
    if (bal <= 3000) return { status: 'low', color: 'text-blue-700 font-semibold bg-blue-50 px-2 py-1 rounded-md' };
    if (bal <= 5000) return { status: 'midlow', color: 'text-amber-700 font-semibold bg-amber-50 px-2 py-1 rounded-md' };
    if (bal <= 10000) return { status: 'medium', color: 'text-purple-700 font-semibold bg-purple-50 px-2 py-1 rounded-md' };
    if (bal <= 15000) return { status: 'high', color: 'text-red-700 font-semibold bg-red-50 px-2 py-1 rounded-md' };

    return { status: 'critical', color: 'text-red-800 font-bold bg-red-100 px-2 py-1 rounded-md border border-red-200' };
};


const MyDriversApp = () => {
    const { user } = useAuth();
    const token = localStorage.getItem('authToken') || 'default-token';
    const navigate = useNavigate();

    // Core data state
    const [drivers, setDrivers] = useState([]);
    const [summaryStats, setSummaryStats] = useState({
        totalDrivers: 0, availableDrivers: 0, assignedDrivers: 0, offDutyDrivers: 0,
        totalBalance: 0, expiringLicenses: 0, expiredLicenses: 0,
    });

    // UI control state
    const [searchTerm, setSearchTerm] = useState('');
    const [searchBy, setSearchBy] = useState('name');
    const [showFilters, setShowFilters] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({
        status: '',
        licenseStatus: ''
    });

    // Delete confirmation state
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    // Global alert state
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ color: '', message: '' });
    const [successMessage, setSuccessMessage] = useState('');

    const [verifyingDriver, setVerifyingDriver] = useState(null);

    // Fix ResizeObserver error
    useEffect(() => {
        return fixResizeObserverError();
    }, []);

    // Centralized API call with consistent auth headers
    const apiCall = useCallback(async (method, endpoint, data = null) => {
        const config = {
            method,
            url: `${rootUrl}${endpoint}`,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        };

        if (data) {
            config.data = data;
        }

        return axios(config);
    }, [token]);

    const fetchSummary = useCallback(async () => {
        try {
            const res = await apiCall('GET', '/drivers/summary');
            const d = res.data?.data || {};
            setSummaryStats({
                totalDrivers: d.total || 0,
                availableDrivers: d.available || 0,
                assignedDrivers: d.assigned || 0,
                offDutyDrivers: d.off_duty || 0,
                totalBalance: parseFloat(d.total_balance) || 0,
                expiringLicenses: d.expiring_licenses || 0,
                expiredLicenses: d.expired_licenses || 0,
            });
        } catch {
            // non-critical — silently ignore
        }
    }, [apiCall]);

    // Fetch drivers with server-side pagination
    const fetchDrivers = useCallback(async () => {
        try {
            const params = {
                page: currentPage,
                limit: pageSize
            };

            // Add filters
            if (filters.status) params.status = filters.status;
            if (filters.licenseStatus) params.licenseStatus = filters.licenseStatus;
            if (searchTerm && searchBy) {
                params.search = searchTerm;
                params.searchBy = searchBy;
            }

            const response = await apiCall('GET', `/drivers?${new URLSearchParams(params)}`);
            const responseData = response.data;
            const data = responseData.data || responseData;

            const mappedData = data.map(item => {
                const license_expiry = item.license_expiry ? item.license_expiry.split('T')[0] : 'no licence exp';
                return {
                    // keep all original fields from API, but normalize/format some for the UI
                    ...item,
                    id: item.id || '',
                    name: item.name || 'No name provided',
                    phone: item.phone || 'No phone provided',
                    id_no: item.id_no || 'Unknown',
                    license_no: item.license_no || 'Unknown',
                    license_expiry,
                    license_verified_on: item.license_verified_on || null,
                    license_verification_status: item.license_verification_status || null,
                    license_sticker_link: item.license_sticker_link || null,
                    referred_by: item.referred_by || 'Unknown',
                    status: item.status || 'No status',
                    bal: item.bal || '0.00',
                    drive_link: item.drive_link || 'provide drive link',
                    input_by: item.input_by || 'Unknown',
                    input_date: item.input_date ? item.input_date.split('T')[0] : 'Unknown',
                    edit_date: item.edit_date ? item.edit_date.split('T')[0] : 'Unknown',
                    edit_by: item.edit_by || 'Unknown',
                    // blacklist fields (ensure boolean)
                    is_blacklisted: !!item.is_blacklisted,
                    blacklist_reason: item.blacklist_reason || null,
                    blacklist_by: item.blacklist_by || null,
                    blacklist_date: item.blacklist_date || null
                };
            });

            setDrivers(mappedData);

            if (responseData.pagination) {
                setTotalPages(responseData.pagination.totalPages);
                setTotalCount(responseData.pagination.totalCount || 0);
            } else {
                setTotalCount(mappedData.length);
            }
        } catch (error) {
            console.error("Error fetching drivers:", error);
            setAlert({ color: "red", message: "Error fetching drivers" });
            setOpen(true);
        }
    }, [currentPage, pageSize, filters, searchTerm, searchBy, apiCall]);

    // Blacklist / Unblacklist handler — called from DriversTable
    const handleBlacklistAction = async (driverId, action = 'blacklist', reason = '') => {
        try {
            const endpoint = `/drivers/${driverId}/${action === 'blacklist' ? 'blacklist' : 'unblacklist'}`;
            await apiCall('POST', endpoint, { reason });
            // Re-fetch drivers to refresh UI without full reload
            await fetchDrivers();
            setSuccessMessage(action === 'blacklist' ? 'Driver blacklisted' : 'Driver unblacklisted');
            setTimeout(() => setSuccessMessage(''), 2000);
            return { success: true };
        } catch (err) {
            const message = err.response?.data?.message || err.message || 'Action failed';
            return { success: false, message };
        }
    };

    useEffect(() => {
        fetchDrivers();
    }, [fetchDrivers]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    const confirmDelete = async () => {
        if (!deleteTargetId) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await apiCall('DELETE', `/drivers/${deleteTargetId}`);
            setDrivers(prev => prev.filter(d => d.id !== deleteTargetId));
            setAlert({ color: 'green', message: 'Driver deleted successfully' });
            setOpen(true);
            setDeleteTargetId(null);
        } catch (error) {
            console.error('Error deleting driver:', error);
            setDeleteError('Error deleting driver. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    // Open car insurance verification modal for a driver
    const handleOpenInsurance = (id) => {
        const driver = drivers.find(d => d.id === id);
        if (driver) {
            setVerifyingDriver(driver);
        }
    };

    const closeInsuranceModal = () => {
        setVerifyingDriver(null);
    };

    // Filter handlers
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({ status: '', licenseStatus: '' });
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Pagination handlers
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Summary statistics

    // Reset to first page when filters change
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [currentPage, totalPages]);

    return (
        <div className="min-h-screen bg-brand-gradient">
            <div className="w-full p-0 sm:p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-5 px-1">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-brand-secondary leading-tight">Drivers</h1>
                        <p className="text-xs text-gray-400 mt-0.5">Fleet overview · live page count</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-gray-400">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                        {summaryStats.totalDrivers} on record
                    </div>
                </div>

                {/* Summary Strip */}
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
                    {/* Total */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-4 border-t-[#FF5733] p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Total</span>
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-50">
                                <FontAwesomeIcon icon={faUsers} className="text-[#FF5733] text-xs" />
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-[#FF5733] leading-none">{summaryStats.totalDrivers}</p>
                        <p className="text-[11px] text-gray-400">All registered drivers</p>
                    </div>

                    {/* Available */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-4 border-t-green-500 p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Available</span>
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-50">
                                <FontAwesomeIcon icon={faCircleCheck} className="text-green-600 text-xs" />
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-green-700 leading-none">{summaryStats.availableDrivers}</p>
                        <p className="text-[11px] text-gray-400">
                            {summaryStats.totalDrivers > 0
                                ? `${Math.round(summaryStats.availableDrivers / summaryStats.totalDrivers * 100)}% of fleet`
                                : 'No data'}
                        </p>
                    </div>

                    {/* Assigned */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-4 border-t-blue-500 p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Assigned</span>
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-50">
                                <FontAwesomeIcon icon={faIdCard} className="text-blue-600 text-xs" />
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-blue-700 leading-none">{summaryStats.assignedDrivers}</p>
                        <p className="text-[11px] text-gray-400">On active deployment</p>
                    </div>

                    {/* Off Duty */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-4 border-t-slate-400 p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Off Duty</span>
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-50">
                                <FontAwesomeIcon icon={faClock} className="text-slate-500 text-xs" />
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-slate-600 leading-none">{summaryStats.offDutyDrivers}</p>
                        <p className="text-[11px] text-gray-400">Off duty / on leave</p>
                    </div>

                    {/* License issues */}
                    <div className={`bg-white rounded-xl border border-gray-100 shadow-sm border-t-4 p-4 flex flex-col gap-2 ${summaryStats.expiredLicenses > 0 ? 'border-t-red-500' : summaryStats.expiringLicenses > 0 ? 'border-t-amber-400' : 'border-t-gray-200'}`}>
                        <div className="flex items-start justify-between">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Licenses</span>
                            <span className={`flex h-7 w-7 items-center justify-center rounded-full ${summaryStats.expiredLicenses > 0 ? 'bg-red-50' : summaryStats.expiringLicenses > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                                <FontAwesomeIcon icon={summaryStats.expiredLicenses > 0 ? faCircleXmark : faTriangleExclamation} className={`text-xs ${summaryStats.expiredLicenses > 0 ? 'text-red-500' : summaryStats.expiringLicenses > 0 ? 'text-amber-500' : 'text-gray-300'}`} />
                            </span>
                        </div>
                        <p className={`text-3xl font-bold leading-none ${summaryStats.expiredLicenses > 0 ? 'text-red-600' : summaryStats.expiringLicenses > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                            {summaryStats.expiredLicenses + summaryStats.expiringLicenses}
                        </p>
                        <p className="text-[11px] text-gray-400">
                            {summaryStats.expiredLicenses > 0 && `${summaryStats.expiredLicenses} expired`}
                            {summaryStats.expiredLicenses > 0 && summaryStats.expiringLicenses > 0 && ' · '}
                            {summaryStats.expiringLicenses > 0 && `${summaryStats.expiringLicenses} expiring`}
                            {summaryStats.expiredLicenses === 0 && summaryStats.expiringLicenses === 0 && 'All clear'}
                        </p>
                    </div>

                    {/* Balance */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-4 border-t-purple-500 p-4 flex flex-col gap-2">
                        <div className="flex items-start justify-between">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Balance</span>
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-50">
                                <FontAwesomeIcon icon={faCoins} className="text-purple-600 text-xs" />
                            </span>
                        </div>
                        <p className="text-2xl font-bold text-purple-700 leading-none">
                            {new Intl.NumberFormat('en-KE', { notation: 'compact', maximumFractionDigits: 1 }).format(summaryStats.totalBalance)}
                        </p>
                        <p className="text-[11px] text-gray-400">Total outstanding (KES)</p>
                    </div>
                </div>

                <Alerty
                    open={open}
                    setOpen={setOpen}
                    color={alert.color}
                    message={alert.message}
                />

                {successMessage && (
                    <div className="bg-brand-success/20 text-brand-success border border-brand-success/30 text-center p-3 rounded-lg mb-4">
                        {successMessage}
                    </div>
                )}

                {/* Filter and Table Container */}
                <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                    {/* Filter Section */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                        <Typography variant="h6" color="blue-gray" className="font-semibold text-brand-secondary">
                            <FontAwesomeIcon icon={faSearch} className="mr-2 text-brand-primary" />
                            Search & Filters
                        </Typography>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={() => setShowFilters(!showFilters)}
                                variant="outlined"
                                size="sm"
                                className="flex items-center gap-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-colors duration-200"
                            >
                                <FontAwesomeIcon icon={faFilter} />
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                            </Button>
                            <Button
                                onClick={fetchDrivers}
                                className="bg-brand-primary hover:bg-brand-primary/90 text-white transition-colors duration-200"
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
                                Refresh
                            </Button>
                            <Button
                                onClick={() => navigate('/drivers/new')}
                                className="bg-brand-success hover:bg-brand-success/90 text-white transition-colors duration-200"
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                New Driver
                            </Button>
                        </div>
                    </div>

                    {/* Search Bar */}
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                        <div className="flex items-center gap-2 flex-1">
                            <select
                                value={searchBy}
                                onChange={(e) => setSearchBy(e.target.value)}
                                className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors duration-200"
                            >
                                {TABLE_COLUMNS.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            <Input
                                label={`Search ${TABLE_COLUMNS.find(t => t.value === searchBy)?.label || ''}`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                icon={<FontAwesomeIcon icon={faSearch} className="text-brand-primary" />}
                                className="flex-1"
                                containerProps={{
                                    className: "focus-within:ring-2 focus-within:ring-brand-primary/20"
                                }}
                            />
                        </div>
                    </div>

                    {/* Advanced Filters */}
                    {showFilters && (
                        <div className="border-t border-gray-200 pt-4 mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors duration-200"
                                >
                                    <option value="">All Status</option>
                                    {STATUS_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>

                                <select
                                    value={filters.licenseStatus}
                                    onChange={(e) => handleFilterChange('licenseStatus', e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors duration-200"
                                >
                                    <option value="">All Licenses</option>
                                    <option value="valid">Valid</option>
                                    <option value="expiring">Expiring Soon (≤ 30 days)</option>
                                    <option value="expired">Expired</option>
                                </select>
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    onClick={clearFilters}
                                    variant="outlined"
                                    size="sm"
                                    className="flex items-center gap-2 border-gray-400 text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                                >
                                    Clear All Filters
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Summary Info */}
                    <div className="flex justify-between bg-gray-50 rounded-lg p-4 mb-6">
                        <Typography variant="small" color="blue-gray" className="text-brand-secondary">
                            {totalCount > 0
                                ? `Showing ${((currentPage - 1) * pageSize) + 1} to ${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} drivers`
                                : 'No drivers found'
                            }
                        </Typography>
                        <Typography variant="small" color="blue-gray">
                            Total Balance: <span className="font-bold text-brand-success">
                                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES' }).format(summaryStats.totalBalance)}
                            </span>
                        </Typography>
                    </div>

                    {/* Table */}
                    <DriversTable
                        drivers={drivers}
                        onEdit={(id) => navigate(`/drivers/${id}/edit`)}
                        onView={() => {}}
                        onDelete={(id) => setDeleteTargetId(id)}
                        onVerify={handleOpenInsurance}
                        onBlacklist={handleBlacklistAction}
                        onRefresh={fetchDrivers}
                    />
                </Card>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <Card className="bg-white rounded-2xl shadow-xl p-4 mt-6 border border-gray-100">
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                            <Typography variant="small" color="blue-gray" className="text-brand-secondary">
                                {totalCount > 0
                                    ? `Showing ${((currentPage - 1) * pageSize) + 1} to ${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} drivers`
                                    : 'No drivers found'
                                }
                            </Typography>

                            <div className="flex items-center gap-2">
                                <Typography variant="small" color="blue-gray" className="text-brand-secondary">Drivers per page:</Typography>
                                <select
                                    value={pageSize}
                                    onChange={(e) => setPageSize(parseInt(e.target.value))}
                                    className="w-16 h-8 px-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors duration-200"
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="flex items-center gap-1 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent transition-colors duration-200"
                                >
                                    Previous
                                </Button>

                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNumber;
                                        if (totalPages <= 5) {
                                            pageNumber = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNumber = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNumber = totalPages - 4 + i;
                                        } else {
                                            pageNumber = currentPage - 2 + i;
                                        }

                                        return (
                                            <Button
                                                key={pageNumber}
                                                variant={currentPage === pageNumber ? "filled" : "outlined"}
                                                size="sm"
                                                onClick={() => goToPage(pageNumber)}
                                                className={`min-w-[2rem] px-2 ${currentPage === pageNumber
                                                    ? 'bg-brand-primary hover:bg-brand-primary/90'
                                                    : 'border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white'
                                                    } transition-colors duration-200`}
                                            >
                                                {pageNumber}
                                            </Button>
                                        );
                                    })}
                                </div>

                                <Button
                                    variant="outlined"
                                    size="sm"
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="flex items-center gap-1 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent transition-colors duration-200"
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </Card>
                )}
            </div>

            {/* Car Insurance Verification Modal */}
            {verifyingDriver && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-20 z-50"
                    onClick={(e) => { if (e.target === e.currentTarget) closeInsuranceModal(); }}
                >
                    <div className="w-full max-w-3xl p-4">
                        <div className="relative">
                            <button
                                onClick={closeInsuranceModal}
                                className="absolute right-2 top-2 text-gray-600 bg-white rounded-full p-1 shadow"
                                aria-label="Close"
                            >
                                ×
                            </button>
                            <DriverLicenseVerification
                                apiCall={apiCall}
                                user={user}
                                initial={{
                                    driver_id: verifyingDriver.id,
                                    driver_name: verifyingDriver.name,
                                    license_no: verifyingDriver.license_no === 'Unknown' ? '' : verifyingDriver.license_no,
                                    license_expiry: verifyingDriver.license_expiry === 'no licence exp' ? '' : verifyingDriver.license_expiry
                                }}
                                onSuccess={(data) => {
                                    setSuccessMessage('License verification recorded');
                                    setTimeout(() => setSuccessMessage(''), 2000);
                                    closeInsuranceModal();
                                    fetchDrivers();
                                }}
                                onClose={closeInsuranceModal}
                            />
                        </div>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={Boolean(deleteTargetId)}
                title="Delete Driver"
                description="This will permanently remove the driver record. This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={() => { setDeleteTargetId(null); setDeleteError(''); }}
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />
        </div>
    );
};
export default MyDriversApp;